/**
 * Notes across a share (decision 76; part 38e log, principles DN2, DN6 and DN7): a role key per group and epoch, wrapped
 * under each reader's person key with its place (share, invite, group, epoch); a note sealed under its group's role key
 * or its thread's person key with its place (share, note, group, thread, epoch, author), so the relay can't move a note
 * between groups, threads, epochs, notes or authors, nor a wrap between invites; and the writer's tag, an HMAC over a
 * writer's note's place and sealed bytes under a key only the share's owner secret gives, which the writer's devices
 * check. The invitee's device (part 38c) opens them with the same functions. WebCrypto is injected.
 */
import { aesKey, field, INVITE_ID_BYTES, INVITE_KEY_BYTES, INVITE_SECRET_BYTES, IV_BYTES, joined, lengthOf, sha256, TAG_BYTES } from './invite.ts';

/** A role key: AES-256 (DN2). */
export const ROLE_KEY_BYTES = INVITE_KEY_BYTES;

/** What a role key's wrap takes: its IV, the key and the tag (well within part 38a's `MAX_WRAP_BYTES`). */
export const ROLE_WRAP_BYTES = IV_BYTES + ROLE_KEY_BYTES + TAG_BYTES;

/** What sealing adds to a note's plaintext: its IV and its tag. */
export const NOTE_SEAL_BYTES = IV_BYTES + TAG_BYTES;

/** The writer's tag (DN7): an HMAC-SHA-256, after a writer's note's sealed bytes. */
export const WRITER_TAG_BYTES = 32;

/** The largest epoch: part 38a's columns are Postgres `integer`. */
export const MAX_EPOCH = 2 ** 31 - 1;

const encoder = new TextEncoder();
const ROLE_WRAP_DOMAIN = encoder.encode('cockatrice/invite/role-key/v1');
const NOTE_DOMAIN = encoder.encode('cockatrice/invite/note/v1');
const TAG_KEY_DOMAIN = encoder.encode('cockatrice/share/writer-tag/v1');

/** `epoch` as four bytes, big-endian. */
function epochBytes(epoch: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, epoch);
  return out;
}

/** An ID that may be absent, so present and absent never read alike: a 0, or a 1 and its bytes. */
function optional(id: Uint8Array | null): Uint8Array<ArrayBuffer> {
  return id === null ? new Uint8Array([0]) : joined(new Uint8Array([1]), id);
}

function isEpoch(epoch: number): boolean {
  return Number.isSafeInteger(epoch) && epoch >= 0 && epoch <= MAX_EPOCH;
}

/** Throws unless `epoch` is one: a caller's mistake. */
function epochOf(epoch: number): void {
  if (!isEpoch(epoch)) throw new RangeError('an epoch must be an integer from 0 to 2^31 - 1');
}

/** Where a role key's wrap belongs (DN2): its share, the invite it is for, its group and epoch. */
export interface RoleKeyPlace {
  readonly share: Uint8Array;
  readonly invite: Uint8Array;
  readonly group: string;
  readonly epoch: number;
}

function roleData(place: RoleKeyPlace): Uint8Array<ArrayBuffer> {
  return joined(ROLE_WRAP_DOMAIN, place.share, place.invite, field(place.group), epochBytes(place.epoch));
}

function rolePlaceOk(place: RoleKeyPlace): boolean {
  return place.share.length === INVITE_ID_BYTES && place.invite.length === INVITE_ID_BYTES && isEpoch(place.epoch);
}

/** DN2: role key `key` wrapped at `place` under the invite's person key `person`, with the fresh IV `iv`. */
export async function wrapRoleKey(subtle: SubtleCrypto, person: Uint8Array<ArrayBuffer>, place: RoleKeyPlace, key: Uint8Array<ArrayBuffer>, iv: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(person, INVITE_KEY_BYTES, 'a person key');
  lengthOf(key, ROLE_KEY_BYTES, 'a role key');
  lengthOf(iv, IV_BYTES, 'an IV');
  lengthOf(place.share, INVITE_ID_BYTES, 'a share ID');
  lengthOf(place.invite, INVITE_ID_BYTES, 'an invite ID');
  epochOf(place.epoch);
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: roleData(place) }, await aesKey(subtle, person), key)));
}

/** DN2: the role key a wrap at `place` holds, opened with person key `person`; null when it doesn't open. */
export async function unwrapRoleKey(subtle: SubtleCrypto, person: Uint8Array<ArrayBuffer>, place: RoleKeyPlace, wrap: Uint8Array): Promise<Uint8Array<ArrayBuffer> | null> {
  if (person.length !== INVITE_KEY_BYTES || !rolePlaceOk(place) || wrap.length !== ROLE_WRAP_BYTES) return null;
  try {
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: wrap.slice(0, IV_BYTES), additionalData: roleData(place) }, await aesKey(subtle, person), wrap.slice(IV_BYTES)));
  } catch {
    return null;
  }
}

/**
 * Where a note belongs (DN6): its share, its ID at the relay, its group (a key group's name, or `person`), the thread (an
 * invite's ID, exactly for `person`), the epoch of its key (0 under a person key) and its author (an invite's ID; null
 * for the writer).
 */
export interface NotePlace {
  readonly share: Uint8Array;
  readonly note: Uint8Array;
  readonly group: string;
  readonly thread: Uint8Array | null;
  readonly epoch: number;
  readonly author: Uint8Array | null;
}

function noteData(place: NotePlace): Uint8Array<ArrayBuffer> {
  return joined(NOTE_DOMAIN, place.share, place.note, field(place.group), optional(place.thread), epochBytes(place.epoch), optional(place.author));
}

function notePlaceOk(place: NotePlace): boolean {
  const id = (bytes: Uint8Array | null): boolean => bytes === null || bytes.length === INVITE_ID_BYTES;
  return place.share.length === INVITE_ID_BYTES && place.note.length === INVITE_ID_BYTES && id(place.thread) && id(place.author) && isEpoch(place.epoch);
}

/** DN6: `plain` sealed as the note at `place` under `key` (a role key or a person key), with the fresh IV `iv`. */
export async function sealNote(subtle: SubtleCrypto, key: Uint8Array<ArrayBuffer>, place: NotePlace, plain: Uint8Array<ArrayBuffer>, iv: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(key, ROLE_KEY_BYTES, 'a note key');
  lengthOf(iv, IV_BYTES, 'an IV');
  if (!notePlaceOk(place)) throw new RangeError('a note place must hold 16-byte IDs and an epoch');
  return joined(iv, new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: noteData(place) }, await aesKey(subtle, key), plain)));
}

/** DN6: the note at `place` opened with `key`; null when it doesn't open (another place's, altered, short). */
export async function openNote(subtle: SubtleCrypto, key: Uint8Array<ArrayBuffer>, place: NotePlace, bytes: Uint8Array): Promise<Uint8Array<ArrayBuffer> | null> {
  if (key.length !== ROLE_KEY_BYTES || !notePlaceOk(place) || bytes.length < NOTE_SEAL_BYTES) return null;
  try {
    return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv: bytes.slice(0, IV_BYTES), additionalData: noteData(place) }, await aesKey(subtle, key), bytes.slice(IV_BYTES)));
  } catch {
    return null;
  }
}

/** DN7: the HMAC key of the writer's tag, from the share's owner secret `secret` (never the owner proof the relay holds). */
async function tagKey(subtle: SubtleCrypto, secret: Uint8Array): Promise<CryptoKey> {
  const raw = await sha256(subtle, joined(TAG_KEY_DOMAIN, secret));
  return subtle.importKey('raw', raw, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify']);
}

/** DN7: the writer's tag of the note at `place` sealed as `sealed`, under the share's owner secret `secret`. */
export async function writerTag(subtle: SubtleCrypto, secret: Uint8Array, place: NotePlace, sealed: Uint8Array): Promise<Uint8Array<ArrayBuffer>> {
  lengthOf(secret, INVITE_SECRET_BYTES, 'an owner secret');
  if (!notePlaceOk(place)) throw new RangeError('a note place must hold 16-byte IDs and an epoch');
  return new Uint8Array(await subtle.sign('HMAC', await tagKey(subtle, secret), joined(noteData(place), sealed)));
}

/** DN7: whether `tag` is the writer's tag of the note at `place` sealed as `sealed`. */
export async function writerTagHolds(subtle: SubtleCrypto, secret: Uint8Array, place: NotePlace, sealed: Uint8Array, tag: Uint8Array<ArrayBuffer>): Promise<boolean> {
  if (secret.length !== INVITE_SECRET_BYTES || !notePlaceOk(place) || tag.length !== WRITER_TAG_BYTES) return false;
  return subtle.verify('HMAC', await tagKey(subtle, secret), tag, joined(noteData(place), sealed));
}
